import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  Tag
} from 'antd';
import {
  ClearOutlined,
  FilterOutlined,
  ReloadOutlined,
  UpOutlined,
  DownOutlined,
  ShopOutlined,
  TagOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  FILTER_TYPES,
  DEFAULT_FILTER_VALUES,
  FilterManager
} from '../constants/filterTypes';
import SearchInput from './SearchInput';

const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const COMPOUND_FIELD_OPTIONS = [
  { value: 'sku', label: 'SKU', type: 'text' },
  { value: 'childCount', label: 'SKU Count in Listing', type: 'number' },
  { value: 'rating', label: 'Rating', type: 'number' },
  { value: 'review_count', label: 'Review', type: 'number' },
  { value: 'mainRank', label: 'Rank', type: 'number' },
  { value: 'normalizedRank', label: 'Rank QC', type: 'number' },
  { value: 'sales', label: 'Sale', type: 'number', hasDays: true },
  { value: 'purchaseDate', label: 'Purchase Date', type: 'date' },
  { value: 'category', label: 'Category', type: 'select' },
  { value: 'niche', label: 'Niche', type: 'select' },
  { value: 'title', label: 'Title', type: 'text' },
  { value: 'asin', label: 'ASIN', type: 'text' }
];

const SALES_PERIOD_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 3650, label: 'All time' }
];

const TEXT_OPERATORS = [
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' }
];

const COMPOUND_GROUP_STYLE = {
  padding: '12px 0',
  borderTop: '1px solid #f0f0f0'
};

const COMPOUND_GROUP_LABEL_STYLE = {
  display: 'block',
  marginBottom: 8,
  fontSize: 12,
  textTransform: 'uppercase'
};

const COMPOUND_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 12,
  alignItems: 'start',
  minWidth: 0
};

const FILTER_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  gap: 16,
  alignItems: 'start',
  minWidth: 0
};

const COMPOUND_FILTER_ITEM_STYLE = {
  minWidth: 0
};

const FILTER_ITEM_STYLE = {
  minWidth: 0
};

const INPUT_STYLE = {
  height: 32,
  border: '1px solid #d9d9d9',
  padding: '4px 11px'
};

const getFieldConfig = (field) => COMPOUND_FIELD_OPTIONS.find(option => option.value === field) || COMPOUND_FIELD_OPTIONS[0];
const EXTRA_FILTER_PREFIX = 'compound-extra-';
const FIXED_FILTER_PREFIX = 'compound-fixed-';

const getDefaultCompoundValue = (config) => {
  if (config.type === 'number') return { min: null, max: null };
  if (config.type === 'date') return { from: '', to: '' };
  if (config.type === 'select') return [];
  return '';
};

const getDefaultCompoundOperator = (config) => config.type === 'text' ? 'contains' : (config.type === 'select' ? 'in' : 'between');

const getRangePickerValue = (value = {}) => {
  if (!value.from && !value.to) return null;
  return [
    value.from ? dayjs(value.from) : null,
    value.to ? dayjs(value.to) : null
  ];
};

const formatCompoundValue = (filter, optionLabelMap = {}) => {
  const config = getFieldConfig(filter.field);
  if (config.type === 'number' && filter.value && typeof filter.value === 'object') {
    return `${filter.value.min ?? '...'} to ${filter.value.max ?? '...'}`;
  }
  if (config.type === 'date' && filter.value && typeof filter.value === 'object') {
    return `${filter.value.from || '...'} to ${filter.value.to || '...'}`;
  }
  if (config.type === 'select') {
    const values = Array.isArray(filter.value) ? filter.value : (filter.value ? [filter.value] : []);
    return values.map(value => optionLabelMap[`${filter.field}:${value}`] || value).join(', ');
  }
  return `${filter.operator || ''} ${filter.value || ''}`.trim();
};

const getCompoundFilter = (filters = [], field) => (filters || []).find(filter => {
  if (filter.id === `${FIXED_FILTER_PREFIX}${field}`) return true;
  return filter.field === field && !String(filter.id || '').startsWith(EXTRA_FILTER_PREFIX);
}) || null;

const isCompoundFilterActive = (filter) => {
  const config = getFieldConfig(filter.field);
  if (config.type === 'number') {
    return (filter.value?.min !== null && filter.value?.min !== undefined)
      || (filter.value?.max !== null && filter.value?.max !== undefined);
  }
  if (config.type === 'date') return Boolean(filter.value?.from || filter.value?.to);
  if (config.type === 'select') return Array.isArray(filter.value) ? filter.value.length > 0 : Boolean(filter.value);
  return filter.value !== '' && filter.value !== null && filter.value !== undefined;
};

const ListingsFilter = ({
  onFilterChange,
  onClearFilters,
  onApplyFilters,
  onReload,
  filters,
  loading
}) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const {
    searchText = DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH],
    searchScope = DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH_SCOPE],
    selectedFulfillmentChannel = DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_CHANNEL],
    selectedStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.STATUS],
    selectedSellerAccount = DEFAULT_FILTER_VALUES[FILTER_TYPES.SELLER_ACCOUNT],
    compoundFilters = DEFAULT_FILTER_VALUES[FILTER_TYPES.COMPOUND_FILTERS],
    sortBy = DEFAULT_FILTER_VALUES[FILTER_TYPES.SORT_BY],
    sortOrder = DEFAULT_FILTER_VALUES[FILTER_TYPES.SORT_ORDER],
    sortDays = DEFAULT_FILTER_VALUES[FILTER_TYPES.SORT_DAYS],
    groupSortBy = DEFAULT_FILTER_VALUES[FILTER_TYPES.GROUP_SORT_BY],
    groupSortOrder = DEFAULT_FILTER_VALUES[FILTER_TYPES.GROUP_SORT_ORDER],
    groupSortDays = DEFAULT_FILTER_VALUES[FILTER_TYPES.GROUP_SORT_DAYS],
    salesPeriod = DEFAULT_FILTER_VALUES[FILTER_TYPES.SALES_PERIOD]
  } = filters;

  const [localFilters, setLocalFilters] = useState({
    searchText,
    searchScope,
    selectedFulfillmentChannel,
    selectedStatus,
    selectedSellerAccount: Array.isArray(selectedSellerAccount) ? selectedSellerAccount : (selectedSellerAccount === 'all' ? [] : selectedSellerAccount ? [selectedSellerAccount] : []),
    compoundFilters: Array.isArray(compoundFilters) ? compoundFilters : [],
    sortBy,
    sortOrder,
    sortDays,
    groupSortBy,
    groupSortOrder,
    groupSortDays,
    salesPeriod
  });

  const { sellerAccounts, categories, niches } = useSelector(state => state.dataCommon);

  const compoundOptionLabelMap = useMemo(() => {
    const labels = {};
    (categories || []).forEach(category => {
      labels[`category:${category.id}`] = category.name;
    });
    (niches || []).forEach(niche => {
      labels[`niche:${niche.id}`] = niche.path || niche.name;
    });
    return labels;
  }, [categories, niches]);

  const filterTypeMap = useMemo(() => ({
    searchText: FILTER_TYPES.SEARCH,
    searchScope: FILTER_TYPES.SEARCH_SCOPE,
    selectedFulfillmentChannel: FILTER_TYPES.FULFILLMENT_CHANNEL,
    selectedStatus: FILTER_TYPES.STATUS,
    selectedSellerAccount: FILTER_TYPES.SELLER_ACCOUNT,
    compoundFilters: FILTER_TYPES.COMPOUND_FILTERS,
    sortBy: FILTER_TYPES.SORT_BY,
    sortOrder: FILTER_TYPES.SORT_ORDER,
    sortDays: FILTER_TYPES.SORT_DAYS,
    groupSortBy: FILTER_TYPES.GROUP_SORT_BY,
    groupSortOrder: FILTER_TYPES.GROUP_SORT_ORDER,
    groupSortDays: FILTER_TYPES.GROUP_SORT_DAYS,
    salesPeriod: FILTER_TYPES.SALES_PERIOD
  }), []);

  useEffect(() => {
    setLocalFilters({
      searchText,
      searchScope,
      selectedFulfillmentChannel,
      selectedStatus,
      selectedSellerAccount: Array.isArray(selectedSellerAccount) ? selectedSellerAccount : (selectedSellerAccount === 'all' ? [] : selectedSellerAccount ? [selectedSellerAccount] : []),
      compoundFilters: Array.isArray(compoundFilters) ? compoundFilters : [],
      sortBy,
      sortOrder,
      sortDays,
      groupSortBy,
      groupSortOrder,
      groupSortDays,
      salesPeriod
    });
  }, [searchText, searchScope, selectedFulfillmentChannel, selectedStatus, selectedSellerAccount, compoundFilters, sortBy, sortOrder, sortDays, groupSortBy, groupSortOrder, groupSortDays, salesPeriod]);

  const handleLocalFilterChange = useCallback((filterKey, value) => {
    if (filterKey === 'salesPeriod') {
      setLocalFilters(prev => ({
        ...prev,
        salesPeriod: value,
        sortDays: value,
        groupSortDays: value,
        compoundFilters: (prev.compoundFilters || []).map(f =>
          getFieldConfig(f.field).hasDays ? { ...f, days: value } : f
        )
      }));
      return;
    }
    setLocalFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  const getSearchTextForAPI = useCallback((text) => {
    if (!text) return '';

    const searchTerms = text.includes('\n')
      ? text.split('\n').map(item => item.trim()).filter(item => item.length > 0)
      : text.split(',').map(item => item.trim()).filter(item => item.length > 0);

    return searchTerms.join(',');
  }, []);

  const handleSearchExecute = useCallback((searchValue) => {
    const apiSearchText = getSearchTextForAPI(searchValue || localFilters.searchText);
    onFilterChange(FILTER_TYPES.SEARCH, apiSearchText);
    onApplyFilters();
  }, [onFilterChange, onApplyFilters, getSearchTextForAPI, localFilters.searchText]);

  const handleClearSearch = useCallback(() => {
    handleLocalFilterChange('searchText', '');
    onFilterChange(FILTER_TYPES.SEARCH, '');
    onApplyFilters();
    onReload({ preserveSelection: false });
  }, [handleLocalFilterChange, onFilterChange, onApplyFilters, onReload]);

  const handleClearFilters = useCallback(() => {
    const defaultFilters = FilterManager.getDefaultFilters();
    setLocalFilters({
      ...defaultFilters,
      selectedSellerAccount: [],
      compoundFilters: []
    });
    onClearFilters();
  }, [onClearFilters]);

  const handleApplyFilters = useCallback(() => {
    const activeCompoundFilters = (localFilters.compoundFilters || []).filter(isCompoundFilterActive);
    [
      { type: FILTER_TYPES.SEARCH, value: localFilters.searchText },
      { type: FILTER_TYPES.SEARCH_SCOPE, value: localFilters.searchScope },
      { type: FILTER_TYPES.FULFILLMENT_CHANNEL, value: localFilters.selectedFulfillmentChannel },
      { type: FILTER_TYPES.STATUS, value: localFilters.selectedStatus },
      { type: FILTER_TYPES.SELLER_ACCOUNT, value: localFilters.selectedSellerAccount },
      { type: FILTER_TYPES.COMPOUND_FILTERS, value: activeCompoundFilters },
      { type: FILTER_TYPES.SORT_BY, value: localFilters.sortBy },
      { type: FILTER_TYPES.SORT_ORDER, value: localFilters.sortOrder },
      { type: FILTER_TYPES.SORT_DAYS, value: localFilters.salesPeriod },
      { type: FILTER_TYPES.GROUP_SORT_BY, value: localFilters.groupSortBy },
      { type: FILTER_TYPES.GROUP_SORT_ORDER, value: localFilters.groupSortOrder },
      { type: FILTER_TYPES.GROUP_SORT_DAYS, value: localFilters.salesPeriod },
      { type: FILTER_TYPES.SALES_PERIOD, value: localFilters.salesPeriod }
    ].forEach(({ type, value }) => onFilterChange(type, value));

    onApplyFilters();
    setIsFilterExpanded(false);
  }, [localFilters, onFilterChange, onApplyFilters]);

  const clearIndividualFilter = useCallback((filterKey) => {
    const defaultValue = filterKey === 'selectedSellerAccount' ? [] : DEFAULT_FILTER_VALUES[filterTypeMap[filterKey]];
    handleLocalFilterChange(filterKey, defaultValue);
  }, [handleLocalFilterChange, filterTypeMap]);

  const handleRemoveFilterTag = useCallback((filterKey) => {
    const filterType = filterTypeMap[filterKey];
    if (!filterType) return;

    const defaultValue = filterKey === 'selectedSellerAccount' ? [] : DEFAULT_FILTER_VALUES[filterType];
    if (filterKey === 'salesPeriod') {
      setLocalFilters(prev => ({ ...prev, salesPeriod: defaultValue, sortDays: defaultValue, groupSortDays: defaultValue }));
    } else {
      setLocalFilters(prev => ({ ...prev, [filterKey]: defaultValue }));
    }
    onFilterChange(filterType, defaultValue);
    onApplyFilters();
  }, [onFilterChange, onApplyFilters, filterTypeMap]);

  const handleRemoveCompoundFilter = useCallback((id) => {
    setLocalFilters(prev => ({
      ...prev,
      compoundFilters: (prev.compoundFilters || []).filter(filter => filter.id !== id)
    }));
  }, []);

  const updateCompoundFilter = useCallback((field, changes) => {
    setLocalFilters(prev => {
      const config = getFieldConfig(field);
      const nextFilters = [...(prev.compoundFilters || [])];
      const fixedId = `${FIXED_FILTER_PREFIX}${field}`;
      const index = nextFilters.findIndex(filter => {
        if (filter.id === fixedId) return true;
        return filter.field === field && !String(filter.id || '').startsWith(EXTRA_FILTER_PREFIX);
      });
      const existing = index >= 0 ? nextFilters[index] : {
        id: fixedId,
        field,
        operator: getDefaultCompoundOperator(config),
        value: getDefaultCompoundValue(config)
      };
      const nextFilter = {
        ...existing,
        ...changes,
        id: fixedId,
        field,
        operator: config.type === 'text'
          ? (changes.operator || existing.operator || 'contains')
          : getDefaultCompoundOperator(config),
        ...(config.hasDays ? { days: changes.days ?? existing.days ?? 30 } : {})
      };

      if (isCompoundFilterActive(nextFilter)) {
        if (index >= 0) nextFilters[index] = nextFilter;
        else nextFilters.push(nextFilter);
      } else if (index >= 0) {
        nextFilters.splice(index, 1);
      }

      return {
        ...prev,
        compoundFilters: nextFilters
      };
    });
  }, []);

  const handleAddCompoundFilter = useCallback(() => {
    const config = getFieldConfig('title');
    setLocalFilters(prev => ({
      ...prev,
      compoundFilters: [
        ...(prev.compoundFilters || []),
        {
          id: `${EXTRA_FILTER_PREFIX}${Date.now()}`,
          field: 'title',
          operator: getDefaultCompoundOperator(config),
          value: getDefaultCompoundValue(config)
        }
      ]
    }));
  }, []);

  const updateExtraCompoundFilter = useCallback((id, changes) => {
    setLocalFilters(prev => ({
      ...prev,
      compoundFilters: (prev.compoundFilters || []).map(filter => {
        if (filter.id !== id) return filter;

        const fieldChanged = changes.field && changes.field !== filter.field;
        const nextField = changes.field || filter.field;
        const config = getFieldConfig(nextField);
        const baseFilter = fieldChanged
          ? {
            ...filter,
            field: nextField,
            operator: getDefaultCompoundOperator(config),
            value: getDefaultCompoundValue(config),
            ...(config.hasDays ? { days: 30 } : {})
          }
          : filter;

        return {
          ...baseFilter,
          ...changes,
          field: nextField,
          operator: config.type === 'text'
            ? (changes.operator || baseFilter.operator || 'contains')
            : getDefaultCompoundOperator(config),
          ...(config.hasDays ? { days: changes.days ?? baseFilter.days ?? 30 } : {})
        };
      })
    }));
  }, []);

  const hasUnappliedChanges = useMemo(() => {
    return FilterManager.hasChanges(localFilters, filters);
  }, [localFilters, filters]);

  const activeFiltersCount = useMemo(() => {
    return FilterManager.getActiveFiltersCount(filters);
  }, [filters]);

  const renderFilterSummary = useMemo(() => {
    const tags = [];

    if (searchText && searchText.trim()) {
      tags.push(
        <Tag key="search" closable color="blue" onClose={() => handleRemoveFilterTag('searchText')}>
          Search: {searchText.length > 20 ? `${searchText.substring(0, 20)}...` : searchText}
        </Tag>
      );
    }

    if (searchScope && searchScope !== 'all') {
      tags.push(
        <Tag key="searchScope" closable color="blue" onClose={() => handleRemoveFilterTag('searchScope')}>
          Scope: {searchScope === 'listing' ? 'Listing' : 'SKU'}
        </Tag>
      );
    }

    if (selectedFulfillmentChannel && selectedFulfillmentChannel !== 'all') {
      tags.push(
        <Tag key="fulfillmentChannel" closable color="orange" onClose={() => handleRemoveFilterTag('selectedFulfillmentChannel')}>
          Channel: {selectedFulfillmentChannel}
        </Tag>
      );
    }

    if (selectedStatus && selectedStatus !== 'all') {
      tags.push(
        <Tag key="status" closable color="green" onClose={() => handleRemoveFilterTag('selectedStatus')}>
          Status: {selectedStatus}
        </Tag>
      );
    }

    if (selectedSellerAccount && Array.isArray(selectedSellerAccount) && selectedSellerAccount.length > 0) {
      const accountNames = selectedSellerAccount.map(id => {
        const account = sellerAccounts?.find(acc => acc.id === id);
        return account ? account.name : id;
      });

      tags.push(
        <Tag key="sellerAccount" closable color="purple" onClose={() => handleRemoveFilterTag('selectedSellerAccount')}>
          Accounts: {accountNames.length > 2 ? `${accountNames.slice(0, 2).join(', ')}... (+${accountNames.length - 2})` : accountNames.join(', ')}
        </Tag>
      );
    }

    if (compoundFilters && Array.isArray(compoundFilters)) {
      compoundFilters.filter(isCompoundFilterActive).forEach((filter, index) => {
        const field = getFieldConfig(filter.field);
        tags.push(
          <Tag
            key={`compound-${filter.id || index}`}
            closable
            color="cyan"
            onClose={() => {
              const next = compoundFilters.filter((item, itemIndex) => {
                if (filter.id) return item.id !== filter.id;
                return itemIndex !== index;
              });
              onFilterChange(FILTER_TYPES.COMPOUND_FILTERS, next);
              onApplyFilters();
            }}
          >
            {field.label}{filter.days ? `/${filter.days === 3650 ? 'All' : `${filter.days}d`}` : ''}: {formatCompoundValue(filter, compoundOptionLabelMap)}
          </Tag>
        );
      });
    }

    if (salesPeriod !== DEFAULT_FILTER_VALUES[FILTER_TYPES.SALES_PERIOD]) {
      const periodLabel = salesPeriod === 3650 ? 'All time' : `${salesPeriod} days`;
      tags.push(
        <Tag key="salesPeriod" closable color="default" onClose={() => handleRemoveFilterTag('salesPeriod')}>
          Period: {periodLabel}
        </Tag>
      );
    }

    return tags;
  }, [searchText, searchScope, selectedFulfillmentChannel, selectedStatus, selectedSellerAccount, compoundFilters, salesPeriod, sellerAccounts, compoundOptionLabelMap, handleRemoveFilterTag, onFilterChange, onApplyFilters]);

  const renderTextCompoundFilter = (field, label, placeholder = label) => {
    const filter = getCompoundFilter(localFilters.compoundFilters, field);
    return (
      <div style={COMPOUND_FILTER_ITEM_STYLE}>
        <Text strong style={{ fontSize: 12 }}>{label}</Text>
        <Space.Compact style={{ width: '100%', marginTop: 8 }}>
          <Select
            style={{ width: '42%' }}
            value={filter?.operator || 'contains'}
            onChange={(operator) => updateCompoundFilter(field, {
              operator,
              value: filter?.value || ''
            })}
          >
            {TEXT_OPERATORS.map(option => <Option key={option.value} value={option.value}>{option.label}</Option>)}
          </Select>
          <input
            value={filter?.value || ''}
            placeholder={placeholder}
            onChange={(event) => updateCompoundFilter(field, {
              operator: filter?.operator || 'contains',
              value: event.target.value
            })}
            style={{ ...INPUT_STYLE, width: '58%', borderLeft: 0, borderRadius: '0 6px 6px 0' }}
          />
        </Space.Compact>
      </div>
    );
  };

  const renderSelectCompoundFilter = (field, label, options = []) => {
    const filter = getCompoundFilter(localFilters.compoundFilters, field);
    const value = Array.isArray(filter?.value) ? filter.value : (filter?.value ? [filter.value] : []);
    return (
      <div style={COMPOUND_FILTER_ITEM_STYLE}>
        <Text strong style={{ fontSize: 12 }}>{label}</Text>
        <Select
          mode="multiple"
          style={{ width: '100%', marginTop: 8 }}
          value={value}
          placeholder={`Select ${label.toLowerCase()}`}
          allowClear
          maxTagCount="responsive"
          showSearch
          loading={!options}
          filterOption={(input, option) => String(option.children || '').toLowerCase().includes(input.toLowerCase())}
          onChange={(nextValue) => updateCompoundFilter(field, {
            operator: 'in',
            value: nextValue
          })}
        >
          {(options || []).map(option => (
            <Option key={option.id} value={option.id}>{option.path || option.name}</Option>
          ))}
        </Select>
      </div>
    );
  };

  const renderNumberCompoundFilter = (field, label) => {
    const filter = getCompoundFilter(localFilters.compoundFilters, field);
    const value = filter?.value || {};
    return (
      <div style={COMPOUND_FILTER_ITEM_STYLE}>
        <Text strong style={{ fontSize: 12 }}>{label}</Text>
        <Space.Compact style={{ width: '100%', marginTop: 8 }}>
          <InputNumber
            style={{ width: '50%' }}
            placeholder="From"
            value={value.min}
            onChange={(nextValue) => updateCompoundFilter(field, {
              value: { ...value, min: nextValue }
            })}
          />
          <InputNumber
            style={{ width: '50%' }}
            placeholder="To"
            value={value.max}
            onChange={(nextValue) => updateCompoundFilter(field, {
              value: { ...value, max: nextValue }
            })}
          />
        </Space.Compact>
      </div>
    );
  };

  const renderDateCompoundFilter = (field, label) => {
    const filter = getCompoundFilter(localFilters.compoundFilters, field);
    const value = filter?.value || {};
    return (
      <div style={COMPOUND_FILTER_ITEM_STYLE}>
        <Text strong style={{ fontSize: 12 }}>{label}</Text>
        <RangePicker
          allowEmpty={[true, true]}
          format="YYYY-MM-DD"
          value={getRangePickerValue(value)}
          onChange={(_, dateStrings) => updateCompoundFilter(field, {
            value: {
              from: dateStrings?.[0] || '',
              to: dateStrings?.[1] || ''
            }
          })}
          style={{ width: '100%', marginTop: 8 }}
        />
      </div>
    );
  };

  const renderSaleCompoundFilter = () => {
    const filter = getCompoundFilter(localFilters.compoundFilters, 'sales');
    const value = filter?.value || {};
    return (
      <div style={COMPOUND_FILTER_ITEM_STYLE}>
        <Text strong style={{ fontSize: 12 }}>Sale</Text>
        <Space.Compact style={{ width: '100%', marginTop: 8 }}>
          <InputNumber
            style={{ width: '50%' }}
            placeholder="From"
            value={value.min}
            onChange={(nextValue) => updateCompoundFilter('sales', {
              value: { ...value, min: nextValue },
              days: localFilters.salesPeriod
            })}
          />
          <InputNumber
            style={{ width: '50%' }}
            placeholder="To"
            value={value.max}
            onChange={(nextValue) => updateCompoundFilter('sales', {
              value: { ...value, max: nextValue },
              days: localFilters.salesPeriod
            })}
          />
        </Space.Compact>
      </div>
    );
  };

  const renderExtraCompoundFilter = (filter) => {
    const config = getFieldConfig(filter.field);
    const value = filter.value || getDefaultCompoundValue(config);

    return (
      <div style={COMPOUND_FILTER_ITEM_STYLE} key={filter.id}>
        <Space.Compact style={{ width: '100%' }}>
          <Select
            style={{ width: 'calc(100% - 32px)' }}
            value={filter.field}
            onChange={(field) => updateExtraCompoundFilter(filter.id, { field })}
          >
            {COMPOUND_FIELD_OPTIONS.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
          <Button
            icon={<CloseCircleOutlined />}
            onClick={() => handleRemoveCompoundFilter(filter.id)}
            style={{ width: 32 }}
          />
        </Space.Compact>

        {config.type === 'text' && (
          <Space.Compact style={{ width: '100%', marginTop: 8 }}>
            <Select
              style={{ width: '42%' }}
              value={filter.operator || 'contains'}
              onChange={(operator) => updateExtraCompoundFilter(filter.id, { operator })}
            >
              {TEXT_OPERATORS.map(option => <Option key={option.value} value={option.value}>{option.label}</Option>)}
            </Select>
            <input
              value={value || ''}
              placeholder={config.label}
              onChange={(event) => updateExtraCompoundFilter(filter.id, { value: event.target.value })}
              style={{ ...INPUT_STYLE, width: '58%', borderLeft: 0, borderRadius: '0 6px 6px 0' }}
            />
          </Space.Compact>
        )}

        {config.type === 'select' && (
          <Select
            mode="multiple"
            style={{ width: '100%', marginTop: 8 }}
            value={Array.isArray(value) ? value : (value ? [value] : [])}
            placeholder={`Select ${config.label.toLowerCase()}`}
            allowClear
            maxTagCount="responsive"
            showSearch
            filterOption={(input, option) => String(option.children || '').toLowerCase().includes(input.toLowerCase())}
            onChange={(nextValue) => updateExtraCompoundFilter(filter.id, {
              operator: 'in',
              value: nextValue
            })}
          >
            {(config.value === 'category' ? (categories || []) : (niches || [])).map(option => (
              <Option key={option.id} value={option.id}>{option.path || option.name}</Option>
            ))}
          </Select>
        )}

        {config.type === 'number' && (
          <Space.Compact style={{ width: '100%', marginTop: 8 }}>
            <InputNumber
              style={{ width: '50%' }}
              placeholder="From"
              value={value.min}
              onChange={(nextValue) => updateExtraCompoundFilter(filter.id, {
                value: { ...value, min: nextValue },
                ...(config.hasDays ? { days: localFilters.salesPeriod } : {})
              })}
            />
            <InputNumber
              style={{ width: '50%' }}
              placeholder="To"
              value={value.max}
              onChange={(nextValue) => updateExtraCompoundFilter(filter.id, {
                value: { ...value, max: nextValue },
                ...(config.hasDays ? { days: localFilters.salesPeriod } : {})
              })}
            />
          </Space.Compact>
        )}

        {config.type === 'date' && (
          <RangePicker
            allowEmpty={[true, true]}
            format="YYYY-MM-DD"
            value={getRangePickerValue(value)}
            onChange={(_, dateStrings) => updateExtraCompoundFilter(filter.id, {
              value: {
                from: dateStrings?.[0] || '',
                to: dateStrings?.[1] || ''
              }
            })}
            style={{ width: '100%', marginTop: 8 }}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
      <Row gutter={16} align="middle">
        <Col flex="auto">
          <Row>
            <Col span={6}>
              <SearchInput
                value={localFilters.searchText}
                onChange={(value) => handleLocalFilterChange('searchText', value)}
                onSearch={handleSearchExecute}
                onClear={handleClearSearch}
                placeholder="Search by SKU, ASIN or product name (use commas for multiple SKUs/ASINs)"
                textareaPlaceholder="Search by SKU, ASIN or product name (one per line or use commas)"
              />
              {localFilters.searchText && (localFilters.searchText.includes(',') || localFilters.searchText.includes('\n')) && (
                <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '4px', fontStyle: 'italic' }}>
                  Searching for {getSearchTextForAPI(localFilters.searchText).split(',').filter(val => val.trim().length > 0).length} items
                </div>
              )}
              {hasUnappliedChanges && (
                <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic', color: '#fa8c16' }}>
                  You have unsaved filter changes. Click Apply Filters to search.
                </Text>
              )}
            </Col>
          </Row>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => onReload({ preserveSelection: true })} loading={loading} title="Reload with current filters">
              Reload
            </Button>
            <Button type={isFilterExpanded ? 'primary' : 'default'} icon={<FilterOutlined />} onClick={() => setIsFilterExpanded(!isFilterExpanded)}>
              Filters
              {activeFiltersCount > 0 && (
                <span style={{ marginLeft: 4, backgroundColor: '#ff4d4f', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'inline-block', textAlign: 'center', fontSize: 12, lineHeight: '18px' }}>
                  {activeFiltersCount}
                </span>
              )}
              {hasUnappliedChanges && <span style={{ marginLeft: 4, backgroundColor: '#fa8c16', borderRadius: '50%', width: 6, height: 6, display: 'inline-block' }} title="You have unsaved changes" />}
              {isFilterExpanded ? <UpOutlined style={{ marginLeft: 4 }} /> : <DownOutlined style={{ marginLeft: 4 }} />}
            </Button>
            {activeFiltersCount > 0 && (
              <Button icon={<ClearOutlined />} onClick={handleClearFilters} title="Clear all filters">
                Clear Filters
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {activeFiltersCount > 0 && !isFilterExpanded && (
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>Active filters:</Text>
            <Space size={4} wrap>{renderFilterSummary}</Space>
          </Col>
        </Row>
      )}

      {isFilterExpanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f0f0f0' }}>
          <div style={FILTER_GRID_STYLE}>
            <div style={FILTER_ITEM_STYLE}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedFulfillmentChannel !== 'all' ? '#1890ff' : undefined }}>
                  <ShopOutlined style={{ marginRight: 4 }} />
                  Fulfillment Channel
                </Text>
                {localFilters.selectedFulfillmentChannel !== 'all' && (
                  <Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={() => clearIndividualFilter('selectedFulfillmentChannel')} style={{ color: '#999', padding: 0, minWidth: 'auto' }} />
                )}
              </div>
              <Select style={{ width: '100%' }} onChange={(value) => handleLocalFilterChange('selectedFulfillmentChannel', value)} value={localFilters.selectedFulfillmentChannel}>
                <Option value="all">All Channels</Option>
                <Option value="FBA">FBA</Option>
                <Option value="FBM">FBM</Option>
              </Select>
            </div>

            <div style={FILTER_ITEM_STYLE}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedStatus !== 'all' ? '#1890ff' : undefined }}>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Status
                </Text>
                {localFilters.selectedStatus !== 'all' && (
                  <Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={() => clearIndividualFilter('selectedStatus')} style={{ color: '#999', padding: 0, minWidth: 'auto' }} />
                )}
              </div>
              <Select style={{ width: '100%' }} onChange={(value) => handleLocalFilterChange('selectedStatus', value)} value={localFilters.selectedStatus}>
                <Option value="all">All Status</Option>
                <Option value="ACTIVE">Active</Option>
                <Option value="INACTIVE">Inactive</Option>
                <Option value="DELETED">Deleted</Option>
              </Select>
            </div>

            <div style={FILTER_ITEM_STYLE}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedSellerAccount?.length > 0 ? '#1890ff' : undefined }}>
                  <ShopOutlined style={{ marginRight: 4 }} />
                  Seller Account
                </Text>
                {localFilters.selectedSellerAccount?.length > 0 && (
                  <Button type="text" size="small" icon={<CloseCircleOutlined />} onClick={() => clearIndividualFilter('selectedSellerAccount')} style={{ color: '#999', padding: 0, minWidth: 'auto' }} />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                onChange={(value) => handleLocalFilterChange('selectedSellerAccount', value)}
                value={localFilters.selectedSellerAccount}
                placeholder="Select accounts"
                allowClear
                maxTagCount="responsive"
                loading={!sellerAccounts}
                showSearch
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
              >
                {sellerAccounts && sellerAccounts.map(account => (
                  <Option key={account.id} value={account.id}>{account.name}</Option>
                ))}
              </Select>
            </div>

            <div style={FILTER_ITEM_STYLE}>
              <Text strong>Search Scope</Text>
              <Select style={{ width: '100%', marginTop: 8 }} onChange={(value) => handleLocalFilterChange('searchScope', value)} value={localFilters.searchScope}>
                <Option value="all">Listings + SKUs</Option>
                <Option value="listing">Listings only</Option>
                <Option value="sku">Child SKUs only</Option>
              </Select>
            </div>

            <div style={FILTER_ITEM_STYLE}>
              <Text strong>Sales Period</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={localFilters.salesPeriod}
                onChange={(value) => handleLocalFilterChange('salesPeriod', value)}
              >
                {SALES_PERIOD_OPTIONS.map(opt => <Option key={opt.value} value={opt.value}>{opt.label}</Option>)}
              </Select>
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <Text strong>Compound Filters</Text>

            <div style={{ ...COMPOUND_GROUP_STYLE, borderTop: 0, paddingTop: 10 }}>
              <Text type="secondary" style={COMPOUND_GROUP_LABEL_STYLE}>Identity</Text>
              <div style={COMPOUND_GRID_STYLE}>
                {renderTextCompoundFilter('sku', 'SKU', 'SKU')}
                {renderTextCompoundFilter('title', 'Title', 'Title')}
                {renderTextCompoundFilter('asin', 'ASIN', 'ASIN')}
                {renderSelectCompoundFilter('category', 'Category', categories)}
                {renderSelectCompoundFilter('niche', 'Niche', niches)}
              </div>
            </div>

            <div style={COMPOUND_GROUP_STYLE}>
              <Text type="secondary" style={COMPOUND_GROUP_LABEL_STYLE}>Metrics</Text>
              <div style={COMPOUND_GRID_STYLE}>
                {renderNumberCompoundFilter('childCount', 'SKU Count in Listing')}
                {renderNumberCompoundFilter('rating', 'Rating')}
                {renderNumberCompoundFilter('review_count', 'Review')}
                {renderNumberCompoundFilter('mainRank', 'Rank')}
                {renderSaleCompoundFilter()}
              </div>
            </div>

            <div style={COMPOUND_GROUP_STYLE}>
              <Text type="secondary" style={COMPOUND_GROUP_LABEL_STYLE}>Dates</Text>
              <div style={COMPOUND_GRID_STYLE}>
                {renderDateCompoundFilter('purchaseDate', 'Purchase Date')}
              </div>
            </div>

            <div style={COMPOUND_GROUP_STYLE}>
              <Text type="secondary" style={COMPOUND_GROUP_LABEL_STYLE}>Additional Conditions</Text>
              <div style={COMPOUND_GRID_STYLE}>
                {(localFilters.compoundFilters || [])
                  .filter(filter => String(filter.id || '').startsWith(EXTRA_FILTER_PREFIX))
                  .map(renderExtraCompoundFilter)}
                <div style={{ ...COMPOUND_FILTER_ITEM_STYLE, display: 'flex', alignItems: 'flex-end' }}>
                  <Button size="small" icon={<PlusOutlined />} onClick={handleAddCompoundFilter}>
                    Add condition
                  </Button>
                </div>
              </div>
            </div>

            {(localFilters.compoundFilters || []).filter(isCompoundFilterActive).length > 0 && (
              <Space size={4} wrap style={{ marginTop: 10 }}>
                {(localFilters.compoundFilters || []).filter(isCompoundFilterActive).map((filter) => {
                  const field = getFieldConfig(filter.field);
                  return (
                    <Tag key={filter.id} closable color="cyan" onClose={() => handleRemoveCompoundFilter(filter.id)}>
                      {field.label}{filter.days ? `/${filter.days === 3650 ? 'All' : `${filter.days}d`}` : ''}: {formatCompoundValue(filter, compoundOptionLabelMap)}
                    </Tag>
                  );
                })}
              </Space>
            )}
          </div>

          <Row justify="end" style={{ marginTop: 16 }}>
            <Col>
              <Space>
                <Button onClick={handleClearFilters}>Clear All</Button>
                <Button type={hasUnappliedChanges ? 'primary' : 'default'} onClick={handleApplyFilters} icon={hasUnappliedChanges ? <CheckOutlined /> : <FilterOutlined />}>
                  {hasUnappliedChanges ? 'Apply Changes' : 'Apply Filters'}
                </Button>
              </Space>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default ListingsFilter;
